import { geolocationService } from '../../services/geolocation/geolocationService';
import {
  GEOLOCATION_HIGH_ACCURACY,
  GEOLOCATION_TIMEOUT_MS,
  GEOLOCATION_MAX_AGE_SHORT_MS,
} from '../../constants/geolocation';

/** Shape of the non-standard iOS 13+ permission-gated DeviceOrientationEvent. */
type DeviceOrientationPermissionApi = {
  requestPermission: () => Promise<'granted' | 'denied' | 'default'>;
};

/** Narrowing guard for the optional iOS `requestPermission` static method. */
function hasRequestPermission(
  candidate: unknown
): candidate is DeviceOrientationPermissionApi {
  return (
    typeof candidate === 'function' &&
    typeof (candidate as { requestPermission?: unknown }).requestPermission ===
      'function'
  );
}

/**
 * Prompt the user (must be called from a user gesture, e.g. the Settings toggle)
 * for geolocation consent, and on iOS also request device-orientation (heading)
 * permission. Returns true only when geolocation consent was granted.
 */
export async function requestLocationConsent(): Promise<boolean> {
  const result = await geolocationService.getCurrentPosition({
    enableHighAccuracy: GEOLOCATION_HIGH_ACCURACY,
    timeout: GEOLOCATION_TIMEOUT_MS,
    maximumAge: GEOLOCATION_MAX_AGE_SHORT_MS,
  });

  const granted = result.success;

  // iOS 13+: heading needs an explicit, gesture-bound orientation permission.
  // Best-effort only — never blocks the geolocation grant result.
  if (granted && hasRequestPermission(DeviceOrientationEvent)) {
    try {
      await DeviceOrientationEvent.requestPermission();
    } catch (error) {
      console.error('DeviceOrientation permission request failed:', error);
    }
  }

  return granted;
}
